import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Phone, Check, Clock, Users, TrainFront } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { LOCATION_PAGES, PROGRAM_SLUGS, SITE_URL } from "../data/siteContent";

export default function LocationLanding({ city }) {
  const loc = LOCATION_PAGES[city];
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, [city]);

  const jsonLd = useMemo(() => {
    if (!loc) return [];
    return [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: loc.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
          {
            "@type": "ListItem",
            position: 2,
            name: `Nursing College in ${loc.city}`,
            item: `${SITE_URL}/${loc.slug}`,
          },
        ],
      },
    ];
  }, [loc]);

  if (!loc) {
    return (
      <div data-testid="location-not-found">
        <PageHero
          overline="Locations"
          title={<>Page not <span className="italic text-gold-gradient">found</span></>}
          subtitle="The page you are looking for does not exist."
        />
        <section className="bg-sand py-24 text-center">
          <Link
            to="/"
            data-testid="location-not-found-home"
            className="inline-flex items-center gap-2 rounded-full bg-navy text-sand font-semibold px-7 py-3.5 transition-transform duration-300 hover:scale-105"
          >
            Back to home <ArrowUpRight size={18} />
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div data-testid={`location-page-${city}`}>
      <SEO title={loc.metaTitle} description={loc.metaDescription} path={`/${loc.slug}`} jsonLd={jsonLd} />

      <PageHero
        overline={`Serving students from ${loc.city}`}
        title={
          <>
            A top nursing college <span className="italic text-gold-gradient">near {loc.city}</span>
          </>
        }
        subtitle={loc.tagline}
      />

      {/* Intro */}
      <section className="bg-sand py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Why SVS</span>
              <h2 className="font-serif text-4xl text-navy mt-4 mb-8">
                Nursing education trusted by {loc.city} families
              </h2>
            </Reveal>
            {loc.intro.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="text-navy/70 leading-relaxed mb-6">{p}</p>
              </Reveal>
            ))}
            <ul className="mt-8 space-y-3">
              {loc.why.map((w, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex gap-3 text-navy/80">
                    <Check size={20} className="text-gold shrink-0" /> {w}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-navy text-sand rounded-[2px] p-8 lg:p-10 relative overflow-hidden" data-testid="location-travel-card">
                <div className="grain-overlay" />
                <div className="relative space-y-6">
                  <h3 className="font-serif text-2xl flex items-center gap-3">
                    <TrainFront size={26} className="text-gold" /> Getting here from {loc.city}
                  </h3>
                  <p className="text-sand/75 text-sm leading-relaxed">{loc.travel}</p>
                  <div className="flex items-start gap-3 border-t border-white/10 pt-6 text-sm">
                    <MapPin size={18} className="text-gold shrink-0 mt-0.5" />
                    <span className="text-sand/80">
                      Bahadurpur, Jainamore, Bokaro Steel City, Jharkhand 829144
                    </span>
                  </div>
                  <a
                    href="tel:+917091355794"
                    data-testid="location-call-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy font-bold px-6 py-4 transition-transform duration-300 hover:scale-[1.03]"
                  >
                    <Phone size={18} /> Call +91 70913 55794
                  </a>
                  <p className="text-center text-sm text-sand/60">
                    Free counselling for {loc.city} students &amp; parents
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="bg-white py-24" data-testid="location-programs">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Programs</span>
              <h2 className="font-serif text-4xl text-navy mt-4">Choose your nursing program</h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                Three INC-aligned pathways — each with 60 seats and a 100% placement guarantee.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08}>
                <Link to={`/programs/${PROGRAM_SLUGS[c.short_name] || ""}`} data-testid={`location-program-${c.short_name}`}>
                  <motion.div
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="group bg-navy text-sand rounded-[2px] p-8 h-full relative overflow-hidden"
                  >
                    <div className="grain-overlay" />
                    <div className="relative">
                      <div className="font-serif text-4xl text-gold/60">{c.short_name}</div>
                      <h3 className="font-serif text-2xl mt-4 mb-3">{c.title}</h3>
                      <div className="space-y-2 text-sm text-sand/60">
                        <span className="flex items-center gap-2"><Clock size={15} className="text-gold" /> {c.duration}</span>
                        <span className="flex items-center gap-2"><Users size={15} className="text-gold" /> {c.seats}</span>
                      </div>
                      <div className="mt-6 flex items-center gap-2 text-gold text-sm font-semibold">
                        View program <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </div>
                    </div>
                  </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-sand py-24" data-testid="location-faq">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Questions</span>
              <h2 className="font-serif text-4xl text-navy mt-4">
                Questions {loc.city} students ask
              </h2>
            </div>
          </Reveal>
          <div className="space-y-5">
            {loc.faqs.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border border-navy/10 rounded-[2px] p-6 bg-white" data-testid={`location-faq-${i}`}>
                  <h3 className="font-serif text-xl text-navy mb-2">{f.q}</h3>
                  <p className="text-navy/60 leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-20 text-center relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-2xl mx-auto px-5">
          <h2 className="font-serif text-3xl sm:text-4xl text-sand mb-6">
            {loc.city} to SVS — <span className="italic text-gold-gradient">your seat awaits</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/admissions"
              data-testid="location-cta-apply"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-8 py-4 transition-transform duration-300 hover:scale-105"
            >
              Apply for 2026-27 <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/admissions/eligibility"
              data-testid="location-cta-eligibility"
              className="inline-flex items-center gap-2 rounded-full border border-sand/40 text-sand font-semibold px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Check eligibility
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
